import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";

const SPREAD_EASE =
  "transform 1400ms cubic-bezier(0.16,1,0.3,1), opacity 1400ms ease-out";

/**
 * The two brand glows behind the sign-in card — blue to the top-left, lime to
 * the bottom-right. They expand from centre to opposite corners on `spread`,
 * then each inner layer keeps tracking the cursor via `par` (parallax).
 * Extracted from `LoginPage.tsx`.
 */
export function LoginAurora({
  spread,
  par,
}: {
  spread: boolean;
  par: { x: number; y: number };
}): JSX.Element {
  const theme = useTheme();
  return (
    <>
      {/* Brand-blue glow — expands from centre to the top-left, then tracks
          the cursor (inner layer). */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          left: "-14%",
          top: "-8%",
          height: "48vmax",
          width: "48vmax",
        }}
        style={{
          transform: spread
            ? "translate3d(0,0,0) scale(1)"
            : "translate3d(46vw,42vh,0) scale(0.35)",
          opacity: spread ? 0.6 : 0.85,
          transition: SPREAD_EASE,
        }}
      >
        <Box
          sx={{ height: "100%", width: "100%", borderRadius: "50%", filter: "blur(90px)" }}
          style={{
            background: `radial-gradient(circle, ${theme.palette.primary.light}, transparent 68%)`,
            transform: `translate3d(${par.x * 26}px, ${par.y * 26}px, 0)`,
            transition: "transform 360ms ease-out",
          }}
        />
      </Box>
      {/* XOP-lime glow — expands from centre to the bottom-right. */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          bottom: "-14%",
          right: "-12%",
          height: "44vmax",
          width: "44vmax",
        }}
        style={{
          transform: spread
            ? "translate3d(0,0,0) scale(1)"
            : "translate3d(-46vw,-42vh,0) scale(0.35)",
          opacity: spread ? 0.5 : 0.8,
          transition: SPREAD_EASE,
        }}
      >
        <Box
          sx={{ height: "100%", width: "100%", borderRadius: "50%", filter: "blur(100px)" }}
          style={{
            background: `radial-gradient(circle, ${theme.palette.secondary.main}, transparent 70%)`,
            transform: `translate3d(${par.x * -34}px, ${par.y * -34}px, 0)`,
            transition: "transform 360ms ease-out",
          }}
        />
      </Box>
    </>
  );
}
